import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export interface QuickActionItem {
  icon: string
  label: string
  action: string
  /** Optional type: 'header' renders as a non-clickable section title */
  type?: 'header'
}

export interface QuickActionsConfig {
  /** Controls visibility without destroying (prevents avatar flicker) */
  visible: boolean
  /** Icon for the primary (left) button */
  primaryIcon: string
  /** Hide the primary (left) button entirely */
  hidePrimary: boolean
  /** Context menu items for the avatar/more button */
  items: QuickActionItem[]
  /** Show filter button */
  showFilter: boolean
  /** Whether the filter is currently active */
  filterActive: boolean
  /** Show search slot */
  showSearch: boolean
  /** Placeholder text for the search input */
  searchPlaceholder: string
  /** Icon for the secondary (middle) button */
  secondaryIcon: string
  /** Context menu items for the secondary button */
  secondaryItems: QuickActionItem[]
  /** Avatar image URL */
  avatarSrc: string | null
  /** Avatar initials fallback */
  avatarInitials: string
  /** Whether there's a gameday banner visible */
  hasBanner: boolean
  /** Whether to include the top menu offset (desktop) */
  hasMenuOffset: boolean
  /** Show highlights play button */
  showHighlights: boolean
  /** Icon for the highlights button */
  highlightsIcon: string
  /** Show minimize button (chat) */
  showMinimize: boolean
  /** Show create/plus button (sub-routes) */
  showCreate: boolean
  /** Icon for the create button */
  createIcon: string
  /** Context menu items for the create button (when set, opens menu instead of calling onCreate) */
  createItems: QuickActionItem[]
  /** Show second create button (sub-routes) */
  showCreate2: boolean
  /** Icon for the second create button */
  createIcon2: string
  /** Show calendar sync button */
  showCalendar: boolean
  /** Icon for the calendar button */
  calendarIcon: string
  /** Context menu items for the calendar button */
  calendarItems: QuickActionItem[]
  /** Show share button (for share/copy link actions) */
  showShare: boolean
  /** Icon for the share button */
  shareIcon: string
  /** Show save button */
  showSave: boolean
  /** Icon for the save button */
  saveIcon: string
  /** Loading state for the save button */
  saveLoading: boolean
  /** Disabled state for the save button */
  saveDisabled: boolean
  /** Show delete button */
  showDelete: boolean
  /** Icon for the delete button */
  deleteIcon: string
}

const DEFAULT_CONFIG: QuickActionsConfig = {
  visible: true,
  primaryIcon: 'mdi-plus',
  hidePrimary: false,
  items: [],
  showFilter: false,
  filterActive: false,
  showSearch: false,
  searchPlaceholder: 'Hledat...',
  secondaryIcon: 'mdi-share-variant-outline',
  secondaryItems: [],
  avatarSrc: null,
  avatarInitials: '',
  hasBanner: true,
  hasMenuOffset: true,
  showHighlights: false,
  highlightsIcon: 'mdi-video-outline',
  showMinimize: false,
  showCreate: false,
  createIcon: 'mdi-plus',
  createItems: [],
  showCreate2: false,
  createIcon2: 'mdi-cash-edit',
  showCalendar: false,
  calendarIcon: 'mdi-calendar-sync-outline',
  calendarItems: [],
  showShare: false,
  shareIcon: 'mdi-share-variant-outline',
  showSave: false,
  saveIcon: 'mdi-content-save-outline',
  saveLoading: false,
  saveDisabled: false,
  showDelete: false,
  deleteIcon: 'mdi-trash-can-outline',
}

export const useQuickActionsStore = defineStore('quickActions', () => {
  // ── Reactive config ──
  const visible = ref(DEFAULT_CONFIG.visible)
  const primaryIcon = ref(DEFAULT_CONFIG.primaryIcon)
  const hidePrimary = ref(DEFAULT_CONFIG.hidePrimary)
  const items = ref<QuickActionItem[]>([])
  const showFilter = ref(DEFAULT_CONFIG.showFilter)
  const filterActive = ref(DEFAULT_CONFIG.filterActive)
  const showSearch = ref(DEFAULT_CONFIG.showSearch)
  const searchPlaceholder = ref(DEFAULT_CONFIG.searchPlaceholder)
  const secondaryIcon = ref(DEFAULT_CONFIG.secondaryIcon)
  const secondaryItems = ref<QuickActionItem[]>([])
  const avatarSrc = ref<string | null>(DEFAULT_CONFIG.avatarSrc)
  const avatarInitials = ref(DEFAULT_CONFIG.avatarInitials)
  const hasBanner = ref(DEFAULT_CONFIG.hasBanner)
  const hasMenuOffset = ref(DEFAULT_CONFIG.hasMenuOffset)
  const showHighlights = ref(DEFAULT_CONFIG.showHighlights)
  const highlightsIcon = ref(DEFAULT_CONFIG.highlightsIcon)
  const showMinimize = ref(DEFAULT_CONFIG.showMinimize)
  const showCreate = ref(DEFAULT_CONFIG.showCreate)
  const createIcon = ref(DEFAULT_CONFIG.createIcon)
  const createItems = ref<QuickActionItem[]>([])
  const showCreate2 = ref(DEFAULT_CONFIG.showCreate2)
  const createIcon2 = ref(DEFAULT_CONFIG.createIcon2)
  const showCalendar = ref(DEFAULT_CONFIG.showCalendar)
  const calendarIcon = ref(DEFAULT_CONFIG.calendarIcon)
  const calendarItems = ref<QuickActionItem[]>([])
  const showShare = ref(DEFAULT_CONFIG.showShare)
  const shareIcon = ref(DEFAULT_CONFIG.shareIcon)
  const showSave = ref(DEFAULT_CONFIG.showSave)
  const saveIcon = ref(DEFAULT_CONFIG.saveIcon)
  const saveLoading = ref(DEFAULT_CONFIG.saveLoading)
  const saveDisabled = ref(DEFAULT_CONFIG.saveDisabled)
  const showDelete = ref(DEFAULT_CONFIG.showDelete)
  const deleteIcon = ref(DEFAULT_CONFIG.deleteIcon)

  // ── Callbacks (set by pages, called by the component) ──
  const onPrimary = ref<(() => void) | null>(null)
  const onAction = ref<((action: string) => void) | null>(null)
  const onHighlights = ref<((e?: MouseEvent) => void) | null>(null)
  const onFilter = ref<(() => void) | null>(null)
  const onSearch = ref<((query: string) => void) | null>(null)
  const onSearchClose = ref<(() => void) | null>(null)
  const onMinimize = ref<(() => void) | null>(null)
  const onCreate = ref<((e?: MouseEvent) => void) | null>(null)
  const onCreate2 = ref<((e?: MouseEvent) => void) | null>(null)
  const onShare = ref<((e?: MouseEvent) => void) | null>(null)
  const onSave = ref<((e?: MouseEvent) => void) | null>(null)
  const onDelete = ref<((e?: MouseEvent) => void) | null>(null)

  // ── Generation counter — incremented on configure(), used by component to detect changes ──
  const generation = ref(0)

  // ── Owner tracking — prevents stale unmount resets from clearing a reclaimed store ──
  const _ownerId = ref(0)

  /**
   * Apply a partial configuration. Merges with defaults for any missing fields.
   * If ownerId is provided, registers that instance as the current owner.
   */
  function configure(config: Partial<QuickActionsConfig> & {
    onPrimary?: (() => void) | null
    onAction?: ((action: string) => void) | null
    onHighlights?: ((e?: MouseEvent) => void) | null
    onFilter?: (() => void) | null
    onSearch?: ((query: string) => void) | null
    onSearchClose?: (() => void) | null
    onMinimize?: (() => void) | null
    onCreate?: ((e?: MouseEvent) => void) | null
    onCreate2?: ((e?: MouseEvent) => void) | null
    onShare?: ((e?: MouseEvent) => void) | null
    onSave?: ((e?: MouseEvent) => void) | null
    onDelete?: ((e?: MouseEvent) => void) | null
  }, ownerId?: number) {
    visible.value = config.visible ?? DEFAULT_CONFIG.visible
    primaryIcon.value = config.primaryIcon ?? DEFAULT_CONFIG.primaryIcon
    hidePrimary.value = config.hidePrimary ?? DEFAULT_CONFIG.hidePrimary
    items.value = config.items ?? []
    showFilter.value = config.showFilter ?? DEFAULT_CONFIG.showFilter
    filterActive.value = config.filterActive ?? DEFAULT_CONFIG.filterActive
    showSearch.value = config.showSearch ?? DEFAULT_CONFIG.showSearch
    searchPlaceholder.value = config.searchPlaceholder ?? DEFAULT_CONFIG.searchPlaceholder
    secondaryIcon.value = config.secondaryIcon ?? DEFAULT_CONFIG.secondaryIcon
    secondaryItems.value = config.secondaryItems ?? []
    avatarSrc.value = config.avatarSrc ?? DEFAULT_CONFIG.avatarSrc
    avatarInitials.value = config.avatarInitials ?? DEFAULT_CONFIG.avatarInitials
    hasBanner.value = config.hasBanner ?? DEFAULT_CONFIG.hasBanner
    hasMenuOffset.value = config.hasMenuOffset ?? DEFAULT_CONFIG.hasMenuOffset
    showHighlights.value = config.showHighlights ?? DEFAULT_CONFIG.showHighlights
    highlightsIcon.value = config.highlightsIcon ?? DEFAULT_CONFIG.highlightsIcon
    showMinimize.value = config.showMinimize ?? DEFAULT_CONFIG.showMinimize
    showCreate.value = config.showCreate ?? DEFAULT_CONFIG.showCreate
    createIcon.value = config.createIcon ?? DEFAULT_CONFIG.createIcon
    createItems.value = config.createItems ?? []
    showCreate2.value = config.showCreate2 ?? DEFAULT_CONFIG.showCreate2
    createIcon2.value = config.createIcon2 ?? DEFAULT_CONFIG.createIcon2
    showCalendar.value = config.showCalendar ?? DEFAULT_CONFIG.showCalendar
    calendarIcon.value = config.calendarIcon ?? DEFAULT_CONFIG.calendarIcon
    calendarItems.value = config.calendarItems ?? []
    showShare.value = config.showShare ?? DEFAULT_CONFIG.showShare
    shareIcon.value = config.shareIcon ?? DEFAULT_CONFIG.shareIcon
    showSave.value = config.showSave ?? DEFAULT_CONFIG.showSave
    saveIcon.value = config.saveIcon ?? DEFAULT_CONFIG.saveIcon
    saveLoading.value = config.saveLoading ?? DEFAULT_CONFIG.saveLoading
    saveDisabled.value = config.saveDisabled ?? DEFAULT_CONFIG.saveDisabled
    showDelete.value = config.showDelete ?? DEFAULT_CONFIG.showDelete
    deleteIcon.value = config.deleteIcon ?? DEFAULT_CONFIG.deleteIcon

    onPrimary.value = config.onPrimary ?? null
    onAction.value = config.onAction ?? null
    onHighlights.value = config.onHighlights ?? null
    onFilter.value = config.onFilter ?? null
    onSearch.value = config.onSearch ?? null
    onSearchClose.value = config.onSearchClose ?? null
    onMinimize.value = config.onMinimize ?? null
    onCreate.value = config.onCreate ?? null
    onCreate2.value = config.onCreate2 ?? null
    onShare.value = config.onShare ?? null
    onSave.value = config.onSave ?? null
    onDelete.value = config.onDelete ?? null

    if (ownerId !== undefined) _ownerId.value = ownerId
    generation.value++
  }

  /**
   * Reset to defaults (called when a page unmounts).
   * If ownerId is provided, only resets if that instance is still the current owner.
   */
  function reset(ownerId?: number) {
    if (ownerId !== undefined && _ownerId.value !== ownerId) return
    configure({})
  }

  /**
   * Patch individual reactive fields without resetting everything.
   * Useful for updating filterActive, items, secondaryItems etc. reactively.
   */
  function patch(partial: Partial<QuickActionsConfig> & {
    onPrimary?: (() => void) | null
    onAction?: ((action: string) => void) | null
    onHighlights?: ((e?: MouseEvent) => void) | null
    onFilter?: (() => void) | null
    onSearch?: ((query: string) => void) | null
    onSearchClose?: (() => void) | null
    onMinimize?: (() => void) | null
    onCreate?: ((e?: MouseEvent) => void) | null
    onCreate2?: ((e?: MouseEvent) => void) | null
    onShare?: ((e?: MouseEvent) => void) | null
    onSave?: ((e?: MouseEvent) => void) | null
    onDelete?: ((e?: MouseEvent) => void) | null
  }) {
    if (partial.visible !== undefined) visible.value = partial.visible
    if (partial.primaryIcon !== undefined) primaryIcon.value = partial.primaryIcon
    if (partial.hidePrimary !== undefined) hidePrimary.value = partial.hidePrimary
    if (partial.items !== undefined) items.value = partial.items
    if (partial.showFilter !== undefined) showFilter.value = partial.showFilter
    if (partial.filterActive !== undefined) filterActive.value = partial.filterActive
    if (partial.showSearch !== undefined) showSearch.value = partial.showSearch
    if (partial.searchPlaceholder !== undefined) searchPlaceholder.value = partial.searchPlaceholder
    if (partial.secondaryIcon !== undefined) secondaryIcon.value = partial.secondaryIcon
    if (partial.secondaryItems !== undefined) secondaryItems.value = partial.secondaryItems
    if (partial.avatarSrc !== undefined) avatarSrc.value = partial.avatarSrc
    if (partial.avatarInitials !== undefined) avatarInitials.value = partial.avatarInitials
    if (partial.hasBanner !== undefined) hasBanner.value = partial.hasBanner
    if (partial.hasMenuOffset !== undefined) hasMenuOffset.value = partial.hasMenuOffset
    if (partial.showHighlights !== undefined) showHighlights.value = partial.showHighlights
    if (partial.highlightsIcon !== undefined) highlightsIcon.value = partial.highlightsIcon
    if (partial.showMinimize !== undefined) showMinimize.value = partial.showMinimize
    if (partial.showCreate !== undefined) showCreate.value = partial.showCreate
    if (partial.createIcon !== undefined) createIcon.value = partial.createIcon
    if (partial.createItems !== undefined) createItems.value = partial.createItems
    if (partial.showCreate2 !== undefined) showCreate2.value = partial.showCreate2
    if (partial.createIcon2 !== undefined) createIcon2.value = partial.createIcon2
    if (partial.onPrimary !== undefined) onPrimary.value = partial.onPrimary
    if (partial.onAction !== undefined) onAction.value = partial.onAction
    if (partial.onHighlights !== undefined) onHighlights.value = partial.onHighlights
    if (partial.onFilter !== undefined) onFilter.value = partial.onFilter
    if (partial.onSearch !== undefined) onSearch.value = partial.onSearch
    if (partial.onSearchClose !== undefined) onSearchClose.value = partial.onSearchClose
    if (partial.onMinimize !== undefined) onMinimize.value = partial.onMinimize
    if (partial.onCreate !== undefined) onCreate.value = partial.onCreate
    if (partial.onCreate2 !== undefined) onCreate2.value = partial.onCreate2
    if (partial.showCalendar !== undefined) showCalendar.value = partial.showCalendar
    if (partial.calendarIcon !== undefined) calendarIcon.value = partial.calendarIcon
    if (partial.calendarItems !== undefined) calendarItems.value = partial.calendarItems
    if (partial.showShare !== undefined) showShare.value = partial.showShare
    if (partial.shareIcon !== undefined) shareIcon.value = partial.shareIcon
    if (partial.onShare !== undefined) onShare.value = partial.onShare
    if (partial.showSave !== undefined) showSave.value = partial.showSave
    if (partial.saveIcon !== undefined) saveIcon.value = partial.saveIcon
    if (partial.saveLoading !== undefined) saveLoading.value = partial.saveLoading
    if (partial.saveDisabled !== undefined) saveDisabled.value = partial.saveDisabled
    if (partial.onSave !== undefined) onSave.value = partial.onSave
    if (partial.showDelete !== undefined) showDelete.value = partial.showDelete
    if (partial.deleteIcon !== undefined) deleteIcon.value = partial.deleteIcon
    if (partial.onDelete !== undefined) onDelete.value = partial.onDelete
  }

  return {
    // State
    visible,
    primaryIcon,
    hidePrimary,
    items,
    showFilter,
    filterActive,
    showSearch,
    searchPlaceholder,
    secondaryIcon,
    secondaryItems,
    avatarSrc,
    avatarInitials,
    hasBanner,
    hasMenuOffset,
    showHighlights,
    highlightsIcon,
    showMinimize,
    showCreate,
    createIcon,
    createItems,
    showCreate2,
    createIcon2,
    showCalendar,
    calendarIcon,
    calendarItems,
    showShare,
    shareIcon,
    showSave,
    saveIcon,
    saveLoading,
    saveDisabled,
    showDelete,
    deleteIcon,
    // Callbacks
    onPrimary,
    onAction,
    onHighlights,
    onFilter,
    onSearch,
    onSearchClose,
    onMinimize,
    onCreate,
    onCreate2,
    onShare,
    onSave,
    onDelete,
    // Meta
    generation,
    _ownerId,
    // Actions
    configure,
    reset,
    patch,
  }
})
